/**
 * DOM sanitizer to prevent XSS attacks
 */
export class DOMSanitizer {
    /**
     * Escape HTML special characters
     * @param {string} text - Text to escape
     * @returns {string} Escaped text safe for HTML
     */
    static escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Escape attribute values
     * @param {string} text - Text to escape
     * @returns {string} Escaped text safe for HTML attributes
     */
    static escapeAttribute(text) {
        if (!text) return '';
        return String(text)
            .replace(/&/g, '&amp;')
            .replace(/'/g, '&#39;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    /**
     * Create a DOM element safely without innerHTML injection
     * @param {string} tag - HTML tag name
     * @param {Object} options - Element options
     * @param {string} options.className - CSS classes
     * @param {string} options.textContent - Text content (will be escaped)
     * @param {Object} options.attributes - Additional attributes
     * @param {Array} options.children - Child elements
     * @returns {HTMLElement} Created element
     */
    static createElement(tag, options = {}) {
        const element = document.createElement(tag);

        if (options.className) {
            element.className = options.className;
        }

        if (options.textContent) {
            element.textContent = options.textContent;
        }

        if (options.attributes) {
            Object.entries(options.attributes).forEach(([key, value]) => {
                element.setAttribute(key, value);
            });
        }

        if (options.children) {
            options.children.forEach(child => {
                if (child) element.appendChild(child);
            });
        }

        return element;
    }

    /**
     * Clear element content safely and replace with new content
     * @param {HTMLElement} element - Element to clear
     * @param {string|HTMLElement|Array<HTMLElement>} content - New content
     */
    static setContent(element, content) {
        if (!element) return;

        // Clear existing content
        element.textContent = '';

        if (typeof content === 'string') {
            element.textContent = content;
        } else if (content instanceof HTMLElement) {
            element.appendChild(content);
        } else if (Array.isArray(content)) {
            content.forEach(child => {
                if (child instanceof HTMLElement) {
                    element.appendChild(child);
                }
            });
        }
    }
}
